import * as i2 from '@ngrx/router-store';
import { routerReducer, getSelectors, StoreRouterConnectingModule } from '@ngrx/router-store';
import * as i1 from '@ngrx/store';
import { createAction, props, createReducer, on, combineReducers, createFeatureSelector, createSelector, select, StoreModule } from '@ngrx/store';
import cloneDeep from 'clone-deep';
import equal$1 from 'fast-deep-equal';
import { v4 } from 'uuid';
import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Optional, Inject, NgModule } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import * as i1$2 from '@angular/router';
import * as i1$1 from '@domgen/dgx-fe-logging';
import { first, mergeMap, firstValueFrom, fromEvent, pipe, map as map$1, timer, merge, isObservable, of, combineLatest, forkJoin } from 'rxjs';
import { HttpParams, HttpContextToken } from '@angular/common/http';
import { map, distinctUntilChanged, filter, withLatestFrom, switchMap, tap, first as first$1, mergeMapTo } from 'rxjs/operators';
import { set, startOfToday } from 'date-fns';
import { EditorContextHelper } from '@magnolia/template-annotations';
import { localStorageSync } from 'ngrx-store-localstorage';

function clone(obj) {
    const dolly = cloneDeep(obj);
    return dolly;
}
function equal(a, b) {
    return equal$1(a, b);
}
function generateId() {
    return v4();
}
function hasNoValue(value) {
    if (Array.isArray(value)) {
        return value.filter((value) => !hasNoValue(value)).length === 0;
    }
    else if (typeof value === 'string') {
        return value.trim() === '';
    }
    return value === null || value === undefined;
}
function hasValue(value) {
    return !hasNoValue(value);
}
function toTitleCase(value) {
    const excludedWords = ['and', 'of', 'on', 'upon', 'with'];
    return value
        .split(/\s+/g)
        .map((word) => {
        word = word.toLowerCase();
        if (excludedWords.includes(word)) {
            return word;
        }
        return word.charAt(0).toUpperCase() + word.substring(1);
    })
        .join(' ');
}
function removeNullValues(value) {
    if (value === null || value === undefined) {
        return undefined;
    }
    if (Array.isArray(value)) {
        return value.map((child) => removeNullValues(child));
    }
    if (typeof value === 'object') {
        return Object.entries(value).reduce((filteredValue, [key, child]) => ({
            ...filteredValue,
            [key]: removeNullValues(child),
        }), value);
    }
    return value;
}
function removeEmptyValues(value) {
    if (value === null || value === undefined) {
        return undefined;
    }
    if (typeof value === 'string' && value.trim() === '') {
        return undefined;
    }
    if (Array.isArray(value)) {
        const filteredArray = value
            .map((child) => removeEmptyValues(child))
            .filter((childValue) => hasValue(childValue));
        return filteredArray.length ? filteredArray : undefined;
    }
    if (typeof value === 'object') {
        const filteredObject = Object.entries(value).reduce((filteredValue, [key, child]) => {
            const childValue = removeEmptyValues(child);
            if (hasNoValue(childValue)) {
                return filteredValue;
            }
            return {
                ...filteredValue,
                [key]: childValue,
            };
        }, {});
        if (Object.keys(filteredObject).length) {
            return filteredObject;
        }
        return undefined;
    }
    return value;
}

const setCorrelationId = createAction('[General] Set Correlation Id', props());

const GENERAL_FEATURE_KEY = 'general';
const initialState = {
    correlationId: generateId(),
};
const generalReducer = createReducer(initialState, on(setCorrelationId, (state, action) => ({
    ...state,
    correlationId: action.correlationId,
})));

const ROUTER_FEATURE_KEY = 'router';

const COMMON_FEATURE_KEY = 'common';
const commonReducer = combineReducers({
    [GENERAL_FEATURE_KEY]: generalReducer,
    [ROUTER_FEATURE_KEY]: routerReducer,
});

const selectCommonState = createFeatureSelector(COMMON_FEATURE_KEY);

const reset = createAction('[Core] Reset');
const sessionInactivityTimeout = createAction('[Core] Session inactivity timeout');

var reset_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    reset: reset,
    sessionInactivityTimeout: sessionInactivityTimeout
});

class ResetFacade {
    constructor(store) {
        this.store = store;
    }
    reset() {
        this.store.dispatch(reset());
    }
    sessionInactivityTimeout() {
        this.store.dispatch(sessionInactivityTimeout());
    }
}
ResetFacade.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ResetFacade, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ResetFacade.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ResetFacade, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ResetFacade, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const SESSION_INACTIVITY_TIMEOUT_RESET_ACTION_CONFIG = {
    action: sessionInactivityTimeout,
};

const RESET_ACTION_CONFIG = {
    action: reset,
};

function resetMetaReducer(...configs) {
    const actionConfigs = [
        ...configs,
        RESET_ACTION_CONFIG,
        SESSION_INACTIVITY_TIMEOUT_RESET_ACTION_CONFIG,
    ];
    return function (reducer) {
        return evaluateState(reducer, ...actionConfigs);
    };
}
function evaluateState(reducer, ...configs) {
    return function (state, action) {
        const config = configs.find((actionConfig) => actionConfig.action.type === action.type);
        const shouldResetState = !config?.evaluation || config.evaluation(action);
        if (config && shouldResetState) {
            return reducer(filterState(state, ...featureKeysToPersist(state, config)), action);
        }
        return reducer(state, action);
    };
}
function featureKeysToPersist(state, config) {
    const resetFeatureKeys = config.resetFeatureKeys;
    if (resetFeatureKeys) {
        return Object.keys(state).filter((key) => !resetFeatureKeys.includes(key));
    }
    else if (config.persistedFeatureKeys) {
        return config.persistedFeatureKeys;
    }
    return [];
}
function filterState(state, ...persistedFeatureKeys) {
    return Object.entries(state).reduce((filteredState, [key, value]) => {
        if (persistedFeatureKeys.includes(key)) {
            return {
                ...filteredState,
                [key]: value,
            };
        }
        return filteredState;
    }, {});
}

var router_actions = /*#__PURE__*/Object.freeze({
    __proto__: null
});

function getRoutePathFromRoot(snapshot) {
    return snapshot.pathFromRoot
        .map((route) => route.url)
        .flat()
        .filter(Boolean)
        .map((urlSegment) => urlSegment.path)
        .reduce((paths, path) => [...paths, `${paths.pop()}/${path}`], ['']);
}
function getRouteUrl(snapshot) {
    const routePath = snapshot.pathFromRoot
        .map((route) => route.url)
        .flat()
        .filter(Boolean)
        .map((urlSegment) => urlSegment.path)
        .join('/')
        .replace(/\/+/g, '/');
    return `/${routePath}`;
}
function getActivatedRoutePath(snapshot) {
    return (snapshot.pathFromRoot
        .map((route) => route.url)
        .flat()
        .filter(Boolean)
        .map((urlSegment) => urlSegment.path)
        .pop() || '');
}
function getActivatedRoute(routerState) {
    let route = routerState.root;
    while (route.firstChild) {
        route = route.firstChild;
    }
    return route;
}
function getRouteData(snapshot) {
    if (!snapshot) {
        return {};
    }
    let data = { ...snapshot.root.data };
    let route = snapshot.root;
    while (route) {
        data = {
            ...data,
            ...route.data,
        };
        route = route.firstChild;
    }
    return data;
}
function getRouteParams(snapshot) {
    if (!snapshot) {
        return {};
    }
    let params = { ...snapshot.root.params };
    let route = snapshot.root;
    while (route) {
        params = {
            ...params,
            ...route.params,
        };
        route = route.firstChild;
    }
    return params;
}

const selectRouterState = createSelector(selectCommonState, (state) => state?.router);
const { selectCurrentRoute, selectFragment, selectQueryParam, selectQueryParams, selectUrl, } = getSelectors(selectRouterState);
const selectRouterSnapshot = createSelector(selectRouterState, (state) => state?.state);
const selectRouteData = createSelector(selectRouterSnapshot, (state) => getRouteData(state));
const selectRouteParams = createSelector(selectRouterSnapshot, (state) => getRouteParams(state));
const selectRouteParam = (param) => createSelector(selectRouteParams, (routeParams) => routeParams[param]);

var router_selectors = /*#__PURE__*/Object.freeze({
    __proto__: null,
    selectRouterState: selectRouterState,
    selectCurrentRoute: selectCurrentRoute,
    selectFragment: selectFragment,
    selectQueryParam: selectQueryParam,
    selectQueryParams: selectQueryParams,
    selectUrl: selectUrl,
    selectRouterSnapshot: selectRouterSnapshot,
    selectRouteData: selectRouteData,
    selectRouteParams: selectRouteParams,
    selectRouteParam: selectRouteParam
});

class RouterFacade {
    constructor(store) {
        this.store = store;
        this.currentRoute$ = this.store.select(selectCurrentRoute);
        this.fragment$ = this.store.select(selectFragment);
        this.queryParams$ = this.store.select(selectQueryParams);
        this.routeData$ = this.store.select(selectRouteData);
        this.routeParams$ = this.store.select(selectRouteParams);
        this.url$ = this.store.select(selectUrl);
    }
    selectRouteParam(param) {
        return this.store.select(selectRouteParam(param));
    }
    selectQueryParam(param) {
        return this.store.select(selectQueryParam(param));
    }
}
RouterFacade.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RouterFacade, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
RouterFacade.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RouterFacade });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RouterFacade, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const selectGeneralState = createSelector(selectCommonState, (state) => state.general);
const selectCorrelationId = createSelector(selectGeneralState, (state) => state.correlationId);

class GeneralFacade {
    constructor(store) {
        this.store = store;
        this.selectCorrelationId$ = this.store.pipe(select(selectCorrelationId));
    }
    setCorrelationId(correlationId) {
        this.store.dispatch(setCorrelationId({ correlationId }));
    }
}
GeneralFacade.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GeneralFacade, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
GeneralFacade.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GeneralFacade });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GeneralFacade, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const BYPASS_ROUTE_GUARD = new InjectionToken('Bypass route guard');

class NoDirectBrowserNavigationGuard {
    constructor(bypassRouteGuard, loggingService, router) {
        this.bypassRouteGuard = bypassRouteGuard;
        this.loggingService = loggingService;
        this.router = router;
    }
    canActivate(route) {
        this.loggingService.debug('NoDirectBrowserNavigationGuard', 'Start');
        const redirectTo = route.data?.redirectTo ?? '/';
        if (this.bypassRouteGuard || this.router.navigated) {
            this.loggingService.debug('NoDirectBrowserNavigationGuard', 'Success');
            return true;
        }
        this.loggingService.debug('NoDirectBrowserNavigationGuard', 'Redirect', redirectTo);
        this.router.navigateByUrl(redirectTo);
        return false;
    }
}
NoDirectBrowserNavigationGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: NoDirectBrowserNavigationGuard, deps: [{ token: BYPASS_ROUTE_GUARD, optional: true }, { token: i1$1.LoggingService }, { token: i1$2.Router }], target: i0.ɵɵFactoryTarget.Injectable });
NoDirectBrowserNavigationGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: NoDirectBrowserNavigationGuard });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: NoDirectBrowserNavigationGuard, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [BYPASS_ROUTE_GUARD]
                }] }, { type: i1$1.LoggingService }, { type: i1$2.Router }]; } });

function isUrl(url) {
    let urlComparison;
    try {
        urlComparison = new URL(url);
    }
    catch (error) {
        return false;
    }
    return urlComparison.protocol === 'http:' || urlComparison.protocol === 'https:';
}
function formatUrl(url) {
    const protocolRegexp = new RegExp(/(http|https):\/{2}/);
    const protocolMatch = url.match(protocolRegexp);
    const protocol = (protocolMatch && protocolMatch[0]) || '';
    const path = url
        .replace(protocolRegexp, '')
        .replace(/\/{2,}/g, '/')
        .replace(/\/+$/, '');
    return `${protocol}${path}`;
}
function buildUrl(url, params = {}) {
    const queryString = httpParamsFromObject(params).toString();
    if (queryString) {
        return `${url}?${queryString}`;
    }
    return url;
}
function httpParamsFromObject(obj) {
    const formattedParams = Object.entries(obj).reduce((params, [key, param]) => {
        const formattedParam = Array.isArray(param)
            ? formatHttpArrayParam(param)
            : formatHttpParam(param);
        if (formattedParam) {
            return {
                ...params,
                [key]: formattedParam,
            };
        }
        return params;
    }, {});
    return new HttpParams({ fromObject: formattedParams });
}
function formatHttpParam(param) {
    switch (typeof param) {
        case 'string':
            return param;
        case 'number':
            return param.toString();
        case 'boolean':
            return param.toString();
        default:
            return undefined;
    }
}
function formatHttpArrayParam(param) {
    return param.reduce((formattedParam, childParam) => {
        const formattedChildParam = formatHttpParam(childParam);
        if (formattedChildParam) {
            return [formattedChildParam, ...formattedParam];
        }
        return formattedParam;
    }, []);
}
function formatHttpPayload(payload) {
    if (!(payload instanceof Object) || payload instanceof Array) {
        return payload;
    }
    return Object.entries(payload).reduce((formattedPayload, [key, property]) => {
        const formattedProperty = formatHttpPayloadProperty(property);
        if (formattedProperty === null || formattedProperty === undefined) {
            return formattedPayload;
        }
        return {
            ...formattedPayload,
            [key]: formattedProperty,
        };
    }, {});
}
function formatHttpPayloadProperty(property) {
    if (Array.isArray(property)) {
        return property
            .map((property) => formatHttpPayloadProperty(property))
            .filter((property) => property !== null && property !== undefined);
    }
    else if (typeof property === 'string') {
        return property === '' ? undefined : property;
    }
    else if (property === null || property === undefined) {
        return property;
    }
    else if (typeof property === 'object') {
        return formatHttpPayload(property);
    }
    return property;
}
function isDomesticAndGeneralApi(url) {
    const domesticAndGeneralDomainPattern = new RegExp(/(domgen)|(domesticandgeneral)/);
    const domesticAndGeneralApiPattern = new RegExp(/(api)/);
    return domesticAndGeneralDomainPattern.test(url) && domesticAndGeneralApiPattern.test(url);
}

class CorrelationIdInterceptor {
    constructor(generalFacade) {
        this.generalFacade = generalFacade;
    }
    intercept(request, next) {
        if (!isDomesticAndGeneralApi(request.url)) {
            return next.handle(request);
        }
        return this.generalFacade.selectCorrelationId$.pipe(first(), mergeMap((correlationId) => {
            const updatedRequest = request.clone({
                headers: request.headers.set('x-correlation-id', correlationId),
            });
            return next.handle(updatedRequest);
        }));
    }
}
CorrelationIdInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CorrelationIdInterceptor, deps: [{ token: GeneralFacade }], target: i0.ɵɵFactoryTarget.Injectable });
CorrelationIdInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CorrelationIdInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CorrelationIdInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: GeneralFacade }]; } });

class FormatPayloadInterceptor {
    constructor() {
        this.formattedHttpMethods = ['POST', 'PATCH', 'PUT'];
    }
    intercept(request, next) {
        if (!this.formattedHttpMethods.includes(request.method)) {
            return next.handle(request);
        }
        const updatedRequest = request.clone({
            body: formatHttpPayload(request.body),
        });
        return next.handle(updatedRequest);
    }
}
FormatPayloadInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FormatPayloadInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FormatPayloadInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FormatPayloadInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FormatPayloadInterceptor, decorators: [{
            type: Injectable
        }] });

const BYPASS_INTERCEPTOR = new HttpContextToken(() => false);

const COMMON_CONFIG = new InjectionToken('Common config');

class RequestActionInterceptor {
    constructor(commonConfig) {
        this.commonConfig = commonConfig;
    }
    intercept(request, next) {
        const requestAction = this.commonConfig.requestAction;
        if (!requestAction || request.context.get(BYPASS_INTERCEPTOR)) {
            return next.handle(request);
        }
        const updatedRequest = request.clone({
            headers: request.headers.set('request-action', requestAction),
        });
        return next.handle(updatedRequest);
    }
}
RequestActionInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RequestActionInterceptor, deps: [{ token: COMMON_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
RequestActionInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RequestActionInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RequestActionInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [COMMON_CONFIG]
                }] }]; } });

class RequestSourceInterceptor {
    constructor(commonConfig) {
        this.commonConfig = commonConfig;
    }
    intercept(request, next) {
        const catalogueStore = this.commonConfig.catalogueStore;
        if (!catalogueStore || request.context.get(BYPASS_INTERCEPTOR)) {
            return next.handle(request);
        }
        const updatedRequest = request.clone({
            headers: request.headers.set('request-source', catalogueStore),
        });
        return next.handle(updatedRequest);
    }
}
RequestSourceInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RequestSourceInterceptor, deps: [{ token: COMMON_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
RequestSourceInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RequestSourceInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RequestSourceInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [COMMON_CONFIG]
                }] }]; } });

class WhiteLabelClientInterceptor {
    constructor(commonConfig) {
        this.commonConfig = commonConfig;
    }
    intercept(request, next) {
        const whiteLabelClient = this.commonConfig.whiteLabelClient;
        if (!whiteLabelClient || request.context.get(BYPASS_INTERCEPTOR)) {
            return next.handle(request);
        }
        const updatedRequest = request.clone({
            headers: request.headers.set('wl-client', whiteLabelClient),
        });
        return next.handle(updatedRequest);
    }
}
WhiteLabelClientInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: WhiteLabelClientInterceptor, deps: [{ token: COMMON_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
WhiteLabelClientInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: WhiteLabelClientInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: WhiteLabelClientInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [COMMON_CONFIG]
                }] }]; } });

class DgxCommonModule {
}
DgxCommonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DgxCommonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DgxCommonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: DgxCommonModule, imports: [CommonModule, i1.StoreFeatureModule, i2.StoreRouterConnectingModule] });
DgxCommonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DgxCommonModule, providers: [
        CorrelationIdInterceptor,
        FormatPayloadInterceptor,
        GeneralFacade,
        NoDirectBrowserNavigationGuard,
        RequestActionInterceptor,
        RequestSourceInterceptor,
        ResetFacade,
        RouterFacade,
        WhiteLabelClientInterceptor,
    ], imports: [CommonModule,
        StoreModule.forFeature(COMMON_FEATURE_KEY, commonReducer),
        StoreRouterConnectingModule.forRoot()] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DgxCommonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        StoreModule.forFeature(COMMON_FEATURE_KEY, commonReducer),
                        StoreRouterConnectingModule.forRoot(),
                    ],
                    providers: [
                        CorrelationIdInterceptor,
                        FormatPayloadInterceptor,
                        GeneralFacade,
                        NoDirectBrowserNavigationGuard,
                        RequestActionInterceptor,
                        RequestSourceInterceptor,
                        ResetFacade,
                        RouterFacade,
                        WhiteLabelClientInterceptor,
                    ],
                    declarations: [],
                    exports: [],
                }]
        }] });

const BreakpointWidth = {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 1024,
    xl: 1280,
};

const themeKeys = new Set([
    'background-alternative',
    'background',
    'decoration-color-1-gradient',
    'decoration-color-1',
    'decoration-color-2-gradient',
    'decoration-color-3-gradient',
    'decoration-color-4-gradient',
    'neutral',
    'neutral-alternative',
    'primary-alternative',
    'primary-gradient',
    'primary',
    'secondary-gradient',
]);

const themes = new Map([
    [
        'background-alternative',
        {
            backgroundColor: 'var(--page-bg--alt)',
            bodyColor: 'var(--text-color)',
            headingColor: 'var(--text-color)',
            emphasisColor: 'var(--text-color)',
        },
    ],
    [
        'background',
        {
            backgroundColor: 'var(--page-bg)',
            bodyColor: 'var(--text-color)',
            headingColor: 'var(--text-color)',
            emphasisColor: 'var(--text-color)',
        },
    ],
    [
        'decoration-color-1-gradient',
        {
            backgroundColor: 'radial-gradient(100% 314.82% at 0% 3.16%, var(--decoration-color-1-500) 0%, var(--decoration-color-1-700) 55.28%, var(--decoration-color-1-800) 100%)',
            bodyColor: 'var(--decoration-color-1-contrast)',
            headingColor: 'var(--decoration-color-1-contrast)',
            emphasisColor: 'var(--decoration-color-1-800)',
        },
    ],
    [
        'decoration-color-1',
        {
            backgroundColor: 'var(--decoration-color-1-700)',
            bodyColor: 'var(--decoration-color-1-contrast)',
            headingColor: 'var(--decoration-color-1-contrast)',
            emphasisColor: 'var(--decoration-color-1-700)',
        },
    ],
    [
        'decoration-color-2-gradient',
        {
            backgroundColor: 'radial-gradient(100% 314.82% at 0% 3.16%, var(--decoration-color-2-500) 0%, var(--decoration-color-2-700) 55.28%, var(--decoration-color-2-900) 100%)',
            bodyColor: 'var(--decoration-color-2-contrast)',
            headingColor: 'var(--decoration-color-2-contrast)',
            emphasisColor: 'var(--decoration-color-2-900)',
        },
    ],
    [
        'decoration-color-3-gradient',
        {
            backgroundColor: 'radial-gradient(100% 314.82% at 0% 3.16%, var(--decoration-color-3-500) 0%, var(--decoration-color-3-700) 55.28%, var(--decoration-color-3-900) 100%)',
            bodyColor: 'var(--decoration-color-3-contrast)',
            headingColor: 'var(--decoration-color-3-contrast)',
            emphasisColor: 'var(--decoration-color-3-900)',
        },
    ],
    [
        'decoration-color-4-gradient',
        {
            backgroundColor: 'radial-gradient(100% 314.82% at 0% 3.16%, var(--decoration-color-4-500) 0%, var(--decoration-color-4-700) 55.28%, var(--decoration-color-4-900) 100%)',
            bodyColor: 'var(--decoration-color-4-contrast)',
            headingColor: 'var(--decoration-color-4-contrast)',
            emphasisColor: 'var(--decoration-color-4-900)',
        },
    ],
    [
        'neutral',
        {
            backgroundColor: 'var(--neutral-200)',
            bodyColor: 'var(--neutral-700)',
            headingColor: 'var(--primary-600)',
            emphasisColor: 'var(--decoration-color-2-600)',
        },
    ],
    [
        'neutral-alternative',
        {
            backgroundColor: 'var(--neutral-500)',
            bodyColor: 'var(--neutral-500-contrast)',
            headingColor: 'var(--neutral-500-contrast)',
            emphasisColor: 'var(--neutral-500-contrast)',
        },
    ],
    [
        'primary-alternative',
        {
            backgroundColor: 'var(--info-card-bg)',
            bodyColor: 'var(--info-card-text)',
            headingColor: 'var(--info-card-text)',
            emphasisColor: 'var(--info-card-text)',
        },
    ],
    [
        'primary-gradient',
        {
            backgroundColor: 'radial-gradient(93.37% 294.27% at 0% 3.16%, var(--primary-500) 0%, var(--primary-700) 53.12%, var(--primary-800) 100%)',
            bodyColor: 'var(--primary-contrast)',
            headingColor: 'var(--primary-contrast)',
            emphasisColor: 'var(--primary-800)',
        },
    ],
    [
        'primary',
        {
            backgroundColor: 'var(--page-bg--primary)',
            bodyColor: 'var(--primary-contrast)',
            headingColor: 'var(--primary-contrast)',
            emphasisColor: 'var(--primary-contrast)',
        },
    ],
    [
        'secondary-gradient',
        {
            backgroundColor: 'radial-gradient(100% 314.82% at 0% 3.16%, var(--secondary-500) 0%, var(--secondary-700) 55.28%, var(--secondary-800) 100%)',
            bodyColor: 'var(--secondary-contrast)',
            headingColor: 'var(--secondary-contrast)',
            emphasisColor: 'var(--secondary-800)',
        },
    ],
]);

const cookieProInitializer = (document, commonConfig, bypassRouteGuard) => () => {
    if (bypassRouteGuard || !commonConfig.cookiePro) {
        return Promise.resolve();
    }
    const head = document.head;
    const script = document.createElement('script');
    script.src = commonConfig.cookiePro.src;
    script.setAttribute('data-domain-script', commonConfig.cookiePro.domainScript);
    head.insertBefore(script, head.childNodes[0]);
    return firstValueFrom(fromEvent(script, 'load'));
};

function routeDataFactory(dataKey) {
    return (route) => route.data.pipe(map((data) => data[dataKey]));
}

function routeSnapshotDataFactory(dataKey) {
    return (route) => {
        return route.snapshot.data[dataKey];
    };
}

const mockGeneralState = {
    correlationId: '1234',
};

const mockData = { param: 1, params: 2 };

const mockParams = { param: 1, params: 2 };

const mockQueryParams = { param: 1, params: 2 };

const mockActivatedRouteSnapshot = {
    params: mockParams,
    data: mockData,
    queryParams: mockQueryParams,
    url: [],
    outlet: 'primary',
    fragment: null,
    firstChild: null,
    children: [],
    parent: null,
    pathFromRoot: [],
    paramMap: {},
    queryParamMap: {},
    component: null,
    routeConfig: {},
    root: {},
};

const mockRouterState = {
    state: {
        root: mockActivatedRouteSnapshot,
        url: '',
    },
    navigationId: 1,
};

const mockCommonState = {
    [GENERAL_FEATURE_KEY]: mockGeneralState,
    [ROUTER_FEATURE_KEY]: mockRouterState,
};

var RequestAction;
(function (RequestAction) {
    RequestAction["AmdeaRegistration"] = "AMDEA";
    RequestAction["HsbcRegistration"] = "HSBC";
    RequestAction["OnlineRegistration"] = "OLR";
    RequestAction["QuoteAndBuy"] = "QandB";
})(RequestAction || (RequestAction = {}));

var RequestSource;
(function (RequestSource) {
    RequestSource["AOCare"] = "AOCARE";
    RequestSource["DomesticAndGeneralIE"] = "DandGIE";
    RequestSource["DomesticAndGeneralUK"] = "DandGUK";
    RequestSource["DomesticAndGeneralUS"] = "DandGUS";
    RequestSource["EDFUK"] = "EDFUK";
    RequestSource["EDFWhiteGoodsUK"] = "EDFWGUK";
    RequestSource["HooverUK"] = "HOOVERUK";
    RequestSource["SkyUK"] = "SKYPUK";
})(RequestSource || (RequestSource = {}));

const mockCommonConfig = {
    requestAction: RequestAction.QuoteAndBuy,
    catalogueStore: RequestSource.DomesticAndGeneralUK,
    sessionInactivityDurationInMinutes: 20,
    whiteLabelClient: RequestSource.DomesticAndGeneralUK,
};

var AddressType;
(function (AddressType) {
    AddressType["Address"] = "Address";
    AddressType["Commercial"] = "Commercial";
    AddressType["Locality"] = "Locality";
    AddressType["Postcode"] = "Postcode";
    AddressType["Residential"] = "Residential";
})(AddressType || (AddressType = {}));

const mockCustomerAddressDetails = {
    addressLine1: '123 Fairwater House',
    addressLine2: '3 Bonnet Street',
    addressLine3: 'London',
    addressLine4: '',
    countryCode: 'GBR',
    countryIso2: 'GB',
    countryIso3: 'GBR',
    countryIsoNumber: 826,
    countryName: 'Great Britain',
    id: 'GB|RM|B|445899',
    postalCode: 'E16 2SZ',
    type: AddressType.Residential,
};

var ContactMethod;
(function (ContactMethod) {
    ContactMethod["Email"] = "EMAIL";
    ContactMethod["Post"] = "POST";
    ContactMethod["SMS"] = "SMS";
    ContactMethod["Telephone"] = "TELEPHONE";
})(ContactMethod || (ContactMethod = {}));

var DocumentSendMethod;
(function (DocumentSendMethod) {
    DocumentSendMethod["Both"] = "BOTH";
    DocumentSendMethod["Email"] = "EMAIL";
    DocumentSendMethod["Post"] = "POST";
})(DocumentSendMethod || (DocumentSendMethod = {}));

var TitleId;
(function (TitleId) {
    TitleId["Miss"] = "Miss";
    TitleId["Mr"] = "Mr";
    TitleId["Mrs"] = "Mrs";
    TitleId["Ms"] = "Ms";
    TitleId["Mx"] = "Mx";
})(TitleId || (TitleId = {}));

const mockCustomerEmailDetails = {
    main: 'bob@ross.com',
};

const mockCustomerMarketingPreferenceDetails = {
    email: true,
    post: true,
    telephone: true,
};

const mockCustomerTelephoneDetails = {
    home: '0123456789',
    mobile: '0123456789',
};

const mockCustomerDetails = {
    address: mockCustomerAddressDetails,
    email: mockCustomerEmailDetails,
    firstName: 'Bob',
    isOverrideAddress: true,
    initial: 'B',
    marketingPreferences: mockCustomerMarketingPreferenceDetails,
    preferredContactMethod: ContactMethod.Email,
    sendMethod: DocumentSendMethod.Email,
    surname: 'Ross',
    telephoneDetails: mockCustomerTelephoneDetails,
    title: TitleId.Mr,
    privacyPolicy: true,
};

const mockNavigationLink = {
    href: 'www.google.com',
    label: 'link',
};

const mockTrustpilotReview = {
    id: '6476f8944b243aa07f4f7bd9',
    author: 'Mr Wayne Bajada',
    title: 'Excellent service',
    content: 'Excellent service. Washing machine broke, booked repair online. Engineer arrived on time and had part on van. All fixed within half an hour.',
    url: 'https://uk.trustpilot.com/reviews/6476f8944b243aa07f4f7bd9',
    stars: 5,
    publishedDate: '2023-05-31T09:34:45Z',
};

var TrustBox;
(function (TrustBox) {
    TrustBox["Button"] = "button";
    TrustBox["Combo"] = "combo";
    TrustBox["List"] = "list";
    TrustBox["LivePreview"] = "livePreview";
    TrustBox["Micro"] = "micro";
    TrustBox["MicroScore"] = "microScore";
    TrustBox["MicroStar"] = "microStar";
    TrustBox["MiniCarousel"] = "miniCarousel";
    TrustBox["Slider"] = "slider";
})(TrustBox || (TrustBox = {}));

const mockTrustpilotConfig = {
    businessUnitId: '4cb3113600006400050dfb02',
    templates: [
        { id: '539ad0ffdec7e10e686debd7', name: TrustBox.MiniCarousel },
        { id: '539ad60defb9600b94d7df2c', name: TrustBox.List },
        { id: '53aa8807dec7e10d38f59f32', name: TrustBox.LivePreview },
        { id: '5419b637fa0340045cd0c936', name: TrustBox.MicroScore },
        { id: '5419b6ffb0d04a076446a9af', name: TrustBox.Combo },
        { id: '5419b6ffb0d04a076446a9af', name: TrustBox.Micro },
        { id: '5419b732fbfb950b10de65e5', name: TrustBox.MicroStar },
        { id: '5419b757fa0340045cd0c938', name: TrustBox.Button },
        { id: '54ad5defc6454f065c28af8b', name: TrustBox.Slider },
    ],
};

var ClientBrandCode;
(function (ClientBrandCode) {
    ClientBrandCode["AEG"] = "AEG";
    ClientBrandCode["Amana"] = "Amana";
    ClientBrandCode["Baumatic"] = "BAUM";
    ClientBrandCode["Beko"] = "BEKO";
    ClientBrandCode["Belling"] = "BELL";
    ClientBrandCode["Blomberg"] = "BLOM";
    ClientBrandCode["Bush"] = "BUSH";
    ClientBrandCode["Candy"] = "CAND";
    ClientBrandCode["Cannon"] = "CANN";
    ClientBrandCode["Cda"] = "CDA";
    ClientBrandCode["Creda"] = "CRED";
    ClientBrandCode["DomesticAndGeneral"] = "DandG";
    ClientBrandCode["Flavel"] = "FLAV";
    ClientBrandCode["Flymo"] = "FLYM";
    ClientBrandCode["Grundig"] = "GRUN";
    ClientBrandCode["Haier"] = "HAIE";
    ClientBrandCode["Hoover"] = "HOOV";
    ClientBrandCode["Hotpoint"] = "HOTP";
    ClientBrandCode["HSBC"] = "HSBC";
    ClientBrandCode["Indesit"] = "INDE";
    ClientBrandCode["LEC"] = "LEC";
    ClientBrandCode["Leisure"] = "LEIS";
    ClientBrandCode["LG"] = "LG";
    ClientBrandCode["Maytag"] = "MAYT";
    ClientBrandCode["MiraShowers"] = "MIRA";
    ClientBrandCode["Stoves"] = "STOV";
    ClientBrandCode["Whirlpool"] = "WHIR";
    ClientBrandCode["Zenith"] = "ZENI";
})(ClientBrandCode || (ClientBrandCode = {}));

var ClientCode;
(function (ClientCode) {
    ClientCode["Amana"] = "00009";
    ClientCode["AEG"] = "00004";
    ClientCode["Baumatic"] = "00029";
    ClientCode["Beko"] = "00034";
    ClientCode["Belling"] = "00035";
    ClientCode["Blomberg"] = "00045";
    ClientCode["Bush"] = "00056";
    ClientCode["Candy"] = "00061";
    ClientCode["Cannon"] = "00062";
    ClientCode["Cda"] = "00000";
    ClientCode["Creda"] = "00077";
    ClientCode["Flavel"] = "00148";
    ClientCode["Flymo"] = "00111";
    ClientCode["Grundig"] = "00129";
    ClientCode["Haier"] = "00118";
    ClientCode["Hoover"] = "00151";
    ClientCode["Hotpoint"] = "00154";
    ClientCode["HSBC"] = "00159";
    ClientCode["Indesit"] = "00170";
    ClientCode["LEC"] = "00191";
    ClientCode["Leisure"] = "00147";
    ClientCode["LG"] = "00194";
    ClientCode["Maytag"] = "00214";
    ClientCode["MiraShowers"] = "00225";
    ClientCode["Stoves"] = "00310";
    ClientCode["Whirlpool"] = "00343";
    ClientCode["Zenith"] = "00161";
})(ClientCode || (ClientCode = {}));

var ClientGroupCode;
(function (ClientGroupCode) {
    ClientGroupCode["ABElectrolux"] = "00168";
    ClientGroupCode["Beko"] = "00030";
    ClientGroupCode["GlenDimplex"] = "00099";
    ClientGroupCode["Haier"] = "00120";
    ClientGroupCode["Husqvarna"] = "00086";
    ClientGroupCode["KohlerMira"] = "00187";
    ClientGroupCode["LG"] = "00157";
    ClientGroupCode["Sainsburys"] = "00045";
    ClientGroupCode["Whirlpool"] = "00138";
})(ClientGroupCode || (ClientGroupCode = {}));

var DomainId;
(function (DomainId) {
    DomainId["Amana"] = "amana";
    DomainId["Baumatic"] = "baumatic";
    DomainId["Beko"] = "beko";
    DomainId["Beko2YearPartsGuarantee"] = "beko2";
    DomainId["Beko10YearPartsGuarantee"] = "beko10";
    DomainId["Belling"] = "belling";
    DomainId["Blomberg"] = "blomberg";
    DomainId["Bush"] = "bush";
    DomainId["Candy"] = "candy";
    DomainId["Candy2YearGuarantee"] = "candy2";
    DomainId["Cda"] = "cda";
    DomainId["Cannon"] = "cannon";
    DomainId["Creda"] = "creda";
    DomainId["DomesticAndGeneral"] = "domestic-and-general";
    DomainId["Flavel"] = "flavel";
    DomainId["Flymo"] = "flymo";
    DomainId["Grundig"] = "grundig";
    DomainId["Haier"] = "haier";
    DomainId["Hoover"] = "hoover";
    DomainId["Hoover2YearGuarantee"] = "hoover2";
    DomainId["Hotpoint"] = "hotpoint";
    DomainId["HSBC"] = "hsbc";
    DomainId["Indesit"] = "indesit";
    DomainId["LEC"] = "lec";
    DomainId["Leisure"] = "leisure";
    DomainId["LG"] = "lg";
    DomainId["Maytag"] = "maytag";
    DomainId["MiraShowers"] = "mira-showers";
    DomainId["Stoves"] = "stoves";
    DomainId["Whirlpool"] = "whirlpool";
    DomainId["Zenith"] = "zenith";
})(DomainId || (DomainId = {}));

var PaymentProfile;
(function (PaymentProfile) {
    PaymentProfile["P"] = "P";
    PaymentProfile["Variable"] = "V";
})(PaymentProfile || (PaymentProfile = {}));

var PaymentTerm;
(function (PaymentTerm) {
    PaymentTerm["FixedTerm"] = "FT";
    PaymentTerm["MPM"] = "MPM";
    PaymentTerm["MPA"] = "MPA";
    PaymentTerm["IndefinitePayMonthly"] = "IPM";
})(PaymentTerm || (PaymentTerm = {}));

var PaymentType;
(function (PaymentType) {
    PaymentType["Card"] = "C";
    PaymentType["DirectDebit"] = "D";
})(PaymentType || (PaymentType = {}));

var TrustBoxTheme;
(function (TrustBoxTheme) {
    TrustBoxTheme["Dark"] = "dark";
    TrustBoxTheme["Light"] = "light";
})(TrustBoxTheme || (TrustBoxTheme = {}));

function distinctUntilChangedDeep() {
    return distinctUntilChanged((previous, current) => {
        return equal$1(previous, current);
    });
}

function distinctUntilKeysChanged(...keys) {
    return distinctUntilChanged((previous, current) => {
        return keys.every((key) => equal$1(previous && previous[key], current && current[key]));
    });
}

function isDefined(value) {
    return value !== undefined && value !== null;
}
function filterNullUndefined() {
    return function (source) {
        return source.pipe(filter(isDefined), map((value) => value));
    };
}

function mapToLatestFrom(source$) {
    return pipe(withLatestFrom(source$), map((values) => values.pop()));
}

function dictionary(items, identifier) {
    return items.reduce((itemDictionary, item) => {
        const key = item[identifier];
        if (typeof key === 'string' || typeof key === 'number') {
            itemDictionary[key] = item;
        }
        return itemDictionary;
    }, {});
}

function toDictionary(identifier) {
    return pipe(filterNullUndefined(), map((items) => dictionary(items, identifier)));
}

class QueryParametersService {
    constructor(activatedRoute, router) {
        this.activatedRoute = activatedRoute;
        this.router = router;
    }
    get(key) {
        return this.activatedRoute.queryParamMap.pipe(first(), map$1((queryParamMap) => queryParamMap.get(key)), map$1((query) => query ?? undefined));
    }
    clear() {
        return this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {},
        });
    }
}
QueryParametersService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: QueryParametersService, deps: [{ token: i1$2.ActivatedRoute }, { token: i1$2.Router }], target: i0.ɵɵFactoryTarget.Injectable });
QueryParametersService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: QueryParametersService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: QueryParametersService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$2.ActivatedRoute }, { type: i1$2.Router }]; } });

class ResetService {
    constructor(commonConfig, document, resetFacade) {
        this.commonConfig = commonConfig;
        this.document = document;
        this.resetFacade = resetFacade;
        this.sessionInactiveByMouseMove$ = fromEvent(this.document, 'mousemove');
        this.sessionInactiveByKeyUp$ = fromEvent(this.document, 'keyup');
    }
    resetSession() {
        return pipe(map(() => this.commonConfig.sessionInactivityDurationInMinutes
            ? this.commonConfig.sessionInactivityDurationInMinutes * 60000
            : undefined), filterNullUndefined(), switchMap((durationInMilliseconds) => timer(durationInMilliseconds)), tap(() => this.resetFacade.sessionInactivityTimeout()));
    }
    detectInactivity$() {
        return merge(this.sessionInactiveByMouseMove$, this.sessionInactiveByKeyUp$).pipe(this.resetSession());
    }
}
ResetService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ResetService, deps: [{ token: COMMON_CONFIG }, { token: DOCUMENT }, { token: ResetFacade }], target: i0.ɵɵFactoryTarget.Injectable });
ResetService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ResetService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ResetService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [COMMON_CONFIG]
                }] }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: ResetFacade }]; } });

const DOMAIN_ID = new InjectionToken('Domain Id');

const WINDOW = new InjectionToken('WindowToken', {
    providedIn: 'root',
    factory: () => {
        if (typeof window === 'undefined' || !window.document) {
            return undefined;
        }
        return window;
    },
});

function combineLatestObj(obj) {
    const entries = Object.entries(obj);
    const sources$ = entries.map(([key, value]) => {
        const value$ = isObservable(value) ? value : of(value);
        return value$.pipe(map((value) => ({ key, value })));
    });
    return combineLatest(sources$).pipe(map((sources) => sources.reduce((result, partial) => ({
        ...result,
        [partial.key]: partial.value,
    }), {})));
}

function retrieveErrorMessage(error) {
    if ('error' in error) {
        return error.error.message;
    }
    return error.message;
}
function retrieveErrorStatusCode(error) {
    if ('error' in error) {
        return error.error.status;
    }
    return;
}

function forkJoinObj(obj) {
    const entries = Object.entries(obj);
    const sources$ = entries.map(([key, value]) => {
        const value$ = isObservable(value) ? value.pipe(first$1()) : of(value);
        return value$.pipe(map((value) => ({ key, value })));
    });
    return forkJoin(sources$).pipe(map((sources) => sources.reduce((result, partial) => ({
        ...result,
        [partial.key]: partial.value,
    }), {})));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function interpolate(value, data) {
    const interpolationRegExp = new RegExp(/{{(\s|&nbsp;)*([\w]*)(\s|&nbsp;)*}}/g);
    const interpolationKeyRegExp = new RegExp(/((\s|&nbsp;)*[{}]+)(\s|&nbsp;)*/g);
    if (Array.isArray(value)) {
        return value.map((_value) => interpolate(_value, data));
    }
    else if (typeof value === 'object' && value !== null && value !== undefined) {
        return Object.keys(value).reduce((interpolated, key) => ({
            ...interpolated,
            [key]: interpolate(value[key], data),
        }), {});
    }
    else if (typeof value !== 'string') {
        return value;
    }
    const interpolationMatches = value.match(interpolationRegExp);
    if (!interpolationMatches) {
        return value;
    }
    return interpolationMatches.reduce((interpolatedValue, interpolationMatch) => {
        const interpolationKey = interpolationMatch.replace(interpolationKeyRegExp, '');
        const interpolationValue = data && data[interpolationKey];
        if (interpolationValue !== undefined && interpolationValue !== null) {
            return interpolatedValue.replace(interpolationMatch, interpolationValue.toString());
        }
        return interpolatedValue.replace(interpolationMatch, '');
    }, value);
}

function round(value = 0, fractionDigits = 0) {
    const multiplier = Math.pow(10, fractionDigits);
    return Math.round(value * multiplier) / multiplier;
}

function formatPurchaseDate(value) {
    return set(startOfToday(), {
        year: value.purchaseYear,
        month: value.purchaseMonth - 1,
        date: value.purchaseDay ?? 1,
    });
}

function selectDispatchIfUndefined(store, selector, action) {
    const selector$ = store.pipe(select(selector));
    return selector$.pipe(first$1(), tap((result) => {
        if (result === null || result === undefined) {
            store.dispatch(action);
        }
    }), mergeMapTo(selector$));
}

function sessionStorageEnabled() {
    try {
        sessionStorage.setItem('session-storage-enabled', 'enabled');
        sessionStorage.removeItem('session-storage-enabled');
        return true;
    }
    catch (error) {
        return false;
    }
}
function memoryStorageSync(reducer) {
    return (state, action) => reducer(state, action);
}
function sessionStorageSync(config) {
    return function (reducer) {
        const isInMagnolia = EditorContextHelper.isSameOrigin() === false;
        const enabled = sessionStorageEnabled();
        if (enabled) {
            return localStorageSync({
                keys: config.keys,
                storage: sessionStorage,
                rehydrate: isInMagnolia ? false : config.rehydrate,
            })(reducer);
        }
        return memoryStorageSync(reducer);
    };
}

/**
 * Generated bundle index. Do not edit.
 */

export { AddressType, BYPASS_INTERCEPTOR, BYPASS_ROUTE_GUARD, BreakpointWidth, COMMON_CONFIG, COMMON_FEATURE_KEY, ClientBrandCode, ClientCode, ClientGroupCode, ContactMethod, CorrelationIdInterceptor, DOMAIN_ID, DgxCommonModule, DocumentSendMethod, DomainId, FormatPayloadInterceptor, NoDirectBrowserNavigationGuard, PaymentProfile, PaymentTerm, PaymentType, QueryParametersService, ROUTER_FEATURE_KEY, RequestAction, RequestActionInterceptor, RequestSource, RequestSourceInterceptor, reset_actions as ResetActions, ResetFacade, ResetService, router_actions as RouterActions, RouterFacade, router_selectors as RouterSelectors, SESSION_INACTIVITY_TIMEOUT_RESET_ACTION_CONFIG, TitleId, TrustBox, TrustBoxTheme, WINDOW, WhiteLabelClientInterceptor, buildUrl, clone, combineLatestObj, commonReducer, cookieProInitializer, dictionary, distinctUntilChangedDeep, distinctUntilKeysChanged, equal, evaluateState, featureKeysToPersist, filterNullUndefined, filterState, forkJoinObj, formatHttpPayload, formatPurchaseDate, formatUrl, generateId, getActivatedRoute, getActivatedRoutePath, getRouteData, getRouteParams, getRoutePathFromRoot, getRouteUrl, hasNoValue, hasValue, httpParamsFromObject, interpolate, isDomesticAndGeneralApi, isUrl, mapToLatestFrom, memoryStorageSync, mockActivatedRouteSnapshot, mockCommonConfig, mockCommonState, mockCustomerAddressDetails, mockCustomerDetails, mockCustomerEmailDetails, mockCustomerMarketingPreferenceDetails, mockCustomerTelephoneDetails, mockData, mockGeneralState, mockNavigationLink, mockParams, mockQueryParams, mockRouterState, mockTrustpilotConfig, mockTrustpilotReview, removeEmptyValues, removeNullValues, resetMetaReducer, retrieveErrorMessage, retrieveErrorStatusCode, round, routeDataFactory, routeSnapshotDataFactory, selectCommonState, selectCurrentRoute, selectDispatchIfUndefined, selectFragment, selectQueryParam, selectQueryParams, selectRouteData, selectRouteParam, selectRouteParams, selectRouterSnapshot, selectRouterState, selectUrl, sessionStorageEnabled, sessionStorageSync, themeKeys, themes, toDictionary, toTitleCase };
